import { DateTime, Settings } from 'luxon';

// Configure Luxon to use UK timezone by default
Settings.defaultZone = 'Europe/London';

/**
 * Normalize a date to Midnight UTC for storage
 * Takes a JavaScript Date and returns ISO 8601 Midnight UTC string
 * 
 * IMPORTANT: This function extracts the date components (year, month, day) as they
 * appear in the user's browser/local timezone, then converts to midnight UTC.
 * This ensures that when a user clicks "January 6" in the calendar, it's stored as
 * "2026-01-06T00:00:00.000Z" regardless of the user's timezone.
 * 
 * @param date - JavaScript Date object from calendar selection
 * @returns ISO 8601 Midnight UTC string (e.g., "2026-01-06T00:00:00.000Z")
 */
export function normalizeToMidnightUTC(date: Date): string {
  // Use 'local' timezone to extract the date components as displayed in the calendar
  // This prevents timezone conversion bugs when users are in different timezones
  const dt = DateTime.fromJSDate(date, { zone: 'local' });
  const dateString = dt.toISODate();
  if (!dateString) {
    throw new Error('Invalid date');
  }
  return `${dateString}T00:00:00.000Z`;
}

/**
 * Convert Midnight UTC string to JavaScript Date for calendar display
 * @param isoString - ISO 8601 Midnight UTC string
 * @returns JavaScript Date object
 */
export function midnightUTCToDate(isoString: string): Date {
  const dt = DateTime.fromISO(isoString, { zone: 'Europe/London' });
  return dt.toJSDate();
}

/**
 * Get today's date in UK timezone as Midnight UTC string
 * @returns ISO 8601 Midnight UTC string for today
 */
export function getTodayMidnightUTC(): string {
  const today = DateTime.local().toISODate();
  if (!today) {
    throw new Error('Failed to get today\'s date');
  }
  return `${today}T00:00:00.000Z`;
}

/**
 * Get current month's start and end dates as Midnight UTC strings
 * @returns Object with start and end date strings
 */
export function getCurrentMonthRange(): { start: string; end: string } {
  const now = DateTime.local();
  const startOfMonth = now.startOf('month');
  const endOfMonth = now.endOf('month');
  
  const start = startOfMonth.toISODate();
  const end = endOfMonth.toISODate();
  
  if (!start || !end) {
    throw new Error('Failed to get month range');
  }
  
  return {
    start: `${start}T00:00:00.000Z`,
    end: `${end}T00:00:00.000Z`,
  };
}

/**
 * Get year and month from a Date object
 * @param date - JavaScript Date object
 * @returns Object with year and month (1-12)
 */
export function getYearMonth(date: Date): { year: number; month: number } {
  const dt = DateTime.fromJSDate(date, { zone: 'Europe/London' });
  return {
    year: dt.year,
    month: dt.month,
  };
}

/**
 * Check if a date is today or in the future (UK timezone)
 * @param date - JavaScript Date object
 * @returns true if date is today or in the future
 */
export function isTodayOrFuture(date: Date): boolean {
  const today = DateTime.local().startOf('day');
  const checkDate = DateTime.fromJSDate(date, { zone: 'Europe/London' }).startOf('day');
  return checkDate >= today;
}

/**
 * Check if a date is in the past (UK timezone)
 * @param date - JavaScript Date object
 * @returns true if date is in the past
 */
export function isPast(date: Date): boolean {
  return !isTodayOrFuture(date);
}

/**
 * Format a date for display (e.g., "5 January 2026")
 */
export function formatDateDisplay(date: Date): string {
  const dt = DateTime.fromJSDate(date, { zone: 'Europe/London' });
  return dt.toFormat('d MMMM yyyy');
}

/**
 * Format a date for short display (e.g., "5 Jan 2026")
 */
export function formatDateShort(date: Date): string {
  const dt = DateTime.fromJSDate(date, { zone: 'Europe/London' });
  return dt.toFormat('d MMM yyyy');
}

/**
 * Format a date for the schedule modal navigation header (e.g., "Wed, 26 Feb 2026")
 */
export function formatDateNavigation(date: Date): string {
  const dt = DateTime.fromJSDate(date, { zone: 'Europe/London' });
  return dt.toFormat('ccc, d MMM yyyy');
}
